import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, serverTimestamp, where, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

// Day names for display
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function useOneOnOneRelationships() {
  const { user, userProfile } = useAuth();
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setRelationships([]);
      setLoading(false);
      return;
    }

    // Query relationships where user is a participant (multi-tenant)
    const q = query(
      collection(db, 'oneOnOneRelationships'),
      where('participants', 'array-contains', user.uid),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setRelationships(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching relationships:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Get only active relationships
  const activeRelationships = relationships.filter(r => r.status === 'active');

  // Get archived relationships
  const archivedRelationships = relationships.filter(r => r.status === 'archived');

  // Add a new 1:1 relationship
  const addRelationship = async (personName, meetingDay = 2, linkedUserId = null, linkedUserEmail = null) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add relationships');
    }

    // Build participants array
    const participants = [user.uid];
    if (linkedUserId) {
      participants.push(linkedUserId);
    }

    const newRelationship = {
      personName,
      meetingDay, // 0=Sun, 1=Mon, 2=Tue, etc.
      status: 'active',
      createdBy: user.uid, // Multi-tenant: creator by Firebase UID
      participants, // Multi-tenant: who can see this 1:1
      linkedUserId: linkedUserId || null, // Link to another user (optional)
      linkedUserEmail: linkedUserEmail || null,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'oneOnOneRelationships'), newRelationship);
    return docRef.id;
  };

  // Update a relationship (name or meeting day)
  const updateRelationship = async (relationshipId, updates) => {
    await updateDoc(doc(db, 'oneOnOneRelationships', relationshipId), updates);
  };

  // Link a relationship to a user
  const linkToUser = async (relationshipId, linkedUserId, linkedUserEmail) => {
    await updateDoc(doc(db, 'oneOnOneRelationships', relationshipId), {
      linkedUserId,
      linkedUserEmail,
      participants: arrayUnion(linkedUserId)
    });
  };

  // Unlink a relationship from a user
  const unlinkUser = async (relationshipId, linkedUserId) => {
    await updateDoc(doc(db, 'oneOnOneRelationships', relationshipId), {
      linkedUserId: null,
      linkedUserEmail: null,
      participants: arrayRemove(linkedUserId)
    });
  };

  // Archive a relationship (soft delete - preserves history)
  const archiveRelationship = async (relationshipId) => {
    await updateDoc(doc(db, 'oneOnOneRelationships', relationshipId), {
      status: 'archived',
      archivedAt: serverTimestamp()
    });
  };

  // Restore an archived relationship
  const restoreRelationship = async (relationshipId) => {
    await updateDoc(doc(db, 'oneOnOneRelationships', relationshipId), {
      status: 'active',
      archivedAt: null
    });
  };

  // Get a relationship by ID
  const getRelationship = useCallback((relationshipId) => {
    return relationships.find(r => r.id === relationshipId);
  }, [relationships]);

  // Get a relationship by person name
  const getRelationshipByPerson = useCallback((personName) => {
    return relationships.find(r => r.personName === personName && r.status === 'active');
  }, [relationships]);

  // Check if current user is the creator of a relationship
  const isCreator = useCallback((relationshipId) => {
    const rel = relationships.find(r => r.id === relationshipId);
    return rel && rel.createdBy === user?.uid;
  }, [relationships, user?.uid]);

  return {
    relationships,
    activeRelationships,
    archivedRelationships,
    loading,
    error,
    addRelationship,
    updateRelationship,
    linkToUser,
    unlinkUser,
    archiveRelationship,
    restoreRelationship,
    getRelationship,
    getRelationshipByPerson,
    isCreator,
    DAY_NAMES
  };
}
